import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('accessToken');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      
      // Set up automatic keep-alive (call every 10 minutes to extend session)
      const keepAliveInterval = setInterval(async () => {
        const currentToken = localStorage.getItem('accessToken');
        if (currentToken) {
          try {
            const response = await axios.post(`${API_BASE}/auth/keep-alive`, {}, {
              headers: { 'Authorization': `Bearer ${currentToken}` }
            });
            
            // Update token if a new one is provided
            if (response.headers['x-new-access-token']) {
              const newToken = response.headers['x-new-access-token'];
              localStorage.setItem('accessToken', newToken);
              axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            }
          } catch (error) {
            // Silently fail - don't interrupt user if keep-alive fails
            // The token refresh will happen on next API call
            console.debug('Keep-alive failed (non-critical):', error.message);
          }
        }
      }, 10 * 60 * 1000); // Every 10 minutes
      
      return () => clearInterval(keepAliveInterval);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`);
      setUser(response.data.user);
    } catch (error) {
      // Token expired or invalid - clear it
      localStorage.removeItem('accessToken');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      // Redirect to login if we get 401
      if (error.response?.status === 401) {
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, {
      email,
      password
    }, {
      withCredentials: true // Important for refresh token cookie
    });

    const { accessToken, user } = response.data;
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    setUser(user);
    return user;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`);
    } catch (error) {
      // Ignore errors
    }
    localStorage.removeItem('accessToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

